import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { crmHeaders } from "../utils/crmApi";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function downloadTextFile(content, filename) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

const UploadEmailCSV = () => {
    const [file, setFile] = useState(null);
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setResponse(null);
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Please select a CSV file.");
            return;
        }

        setLoading(true);
        setResponse(null);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await axios.post(`${BASE_URL}/upload-emails`, formData, {
                headers: { ...crmHeaders(), "Content-Type": "multipart/form-data" },
            });
            setResponse(res.data);
            toast.success(
                `Done: ${res.data.valid} valid, ${res.data.invalid} removed${
                    res.data.duplicatesRemoved ? `, ${res.data.duplicatesRemoved} duplicates skipped` : ""
                }`
            );
            if (res.data.valid > 0 && res.data.validCsvContent) {
                downloadTextFile(res.data.validCsvContent, "valid_emails_clean.csv");
                toast.info("Clean valid sheet downloaded automatically");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to upload file");
        } finally {
            setLoading(false);
        }
    };

    const downloadCleanValidSheet = () => {
        if (!response?.validCsvContent) {
            toast.error("No clean valid sheet available.");
            return;
        }
        downloadTextFile(response.validCsvContent, "valid_emails_clean.csv");
    };

    const downloadInvalidSheet = () => {
        if (!response?.invalidCsvContent) {
            toast.error("No invalid emails to download.");
            return;
        }
        downloadTextFile(response.invalidCsvContent, "removed_invalid_emails.csv");
    };

    const visibleRows = (response?.processedData || []).filter((row) => {
        if (filter === "valid") return row.status === "Valid";
        if (filter === "invalid") return row.status !== "Valid";
        return true;
    });

    return (
        <div>
            <div className="crm-user-form-card mb-4">
                <div className="crm-user-form-card-header">
                    <i className="bi bi-shield-check"></i>
                    <div>
                        <h5>Professional Email Validator</h5>
                        <span>
                            Format · typo · disposable · fake usernames · DNS · MX · SMTP mailbox check ·
                            catch-all detection · clean sheet for bulk send
                        </span>
                    </div>
                </div>
                <div className="crm-user-form-card-body">
                    <p className="text-muted mb-3">
                        Upload CSV: <strong>email | clientName | businessName</strong> (max 5000 rows).
                        Fake, disposable, typo domains, catch-all &amp; non-existent mailboxes are removed.
                        Clean valid-only CSV downloads automatically.
                    </p>
                    <div className="row g-3 align-items-end">
                        <div className="col-md-8">
                            <input type="file" accept=".csv" className="form-control" onChange={handleFileChange} />
                        </div>
                        <div className="col-md-4">
                            <button className="btn btn-primary w-100" onClick={handleUpload} disabled={loading}>
                                {loading ? "Validating..." : "Validate & Clean Sheet"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {response && (
                <div className="crm-follow-table-wrap">
                    <div className="p-3 border-bottom">
                        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3">
                            <div className="d-flex gap-2 flex-wrap">
                                <span className="crm-badge crm-badge--orange">Checked: {response.total}</span>
                                <span className="badge bg-success">Valid: {response.valid}</span>
                                <span className="badge bg-danger">Removed: {response.invalid}</span>
                                {response.duplicatesRemoved > 0 && (
                                    <span className="badge bg-secondary">
                                        Duplicates skipped: {response.duplicatesRemoved}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <button
                                    className="btn btn-success"
                                    onClick={downloadCleanValidSheet}
                                    disabled={!response.valid}
                                >
                                    <i className="bi bi-download me-1"></i>
                                    Download Clean Valid Sheet
                                </button>
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={downloadInvalidSheet}
                                    disabled={!response.invalid}
                                >
                                    Download Removed List
                                </button>
                            </div>
                        </div>
                        <p className="text-muted mb-0 small">
                            Clean sheet columns: <strong>email, clientName, businessName</strong> — ready for Email
                            Sender. Each address is checked for real mailbox (SMTP), not just domain existence.
                        </p>
                    </div>

                    <div className="p-3 border-bottom d-flex flex-wrap gap-2 align-items-center">
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 160 }}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="all">Show all results</option>
                            <option value="valid">Valid only</option>
                            <option value="invalid">Removed only</option>
                        </select>
                    </div>

                    <div className="table-responsive" style={{ maxHeight: 520 }}>
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Domain</th>
                                    <th>Client</th>
                                    <th>Business</th>
                                    <th>Result</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">
                                            No rows in this filter
                                        </td>
                                    </tr>
                                ) : (
                                    visibleRows.map((row, index) => (
                                        <tr key={`${row.email}-${index}`}>
                                            <td>{row.email}</td>
                                            <td>{row.domain}</td>
                                            <td>{row.clientName}</td>
                                            <td>{row.businessName}</td>
                                            <td>
                                                <span
                                                    className={`badge ${
                                                        row.status === "Valid" ? "bg-success" : "bg-danger"
                                                    }`}
                                                >
                                                    {row.status === "Valid" ? "Kept" : "Removed"}
                                                </span>
                                            </td>
                                            <td>{row.reason}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadEmailCSV;
